import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { messaggioDi } from "@/lib/messaggio-errore";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Gli omaggi di casa: quali si offrono nei preventivi e quali partono già
 * accesi. Tocca solo i preventivi nuovi: quelli già fatti tengono i loro.
 * L'omaggio fuori catalogo si scrive nel preventivo, non qui.
 * Ogni cambio resta nel Registro.
 */
export default function OmaggiTab() {
  const [righe, setRighe] = useState(null);
  const [errore, setErrore] = useState(null);
  const [inVolo, setInVolo] = useState(null);

  useEffect(() => {
    api
      .get("/impostazioni/omaggi")
      .then((r) => setRighe(r.data || []))
      .catch((e) => setErrore(messaggioDi(e)));
  }, []);

  const cambia = async (codice, campo, valore) => {
    setInVolo(`${codice}:${campo}`);
    try {
      const r = await api.put(`/impostazioni/omaggi/${codice}`, { [campo]: valore });
      setRighe((xs) => xs.map((x) => (x.codice === codice ? { ...x, ...r.data } : x)));
      toast.success(
        campo === "attivo"
          ? valore
            ? "Da ora si offre nei preventivi nuovi."
            : "Non si offre più nei preventivi nuovi."
          : valore
            ? "Parte già acceso nei preventivi nuovi."
            : "Parte spento: il venditore lo accende se serve.",
      );
    } catch (e) {
      toast.error(messaggioDi(e));
    } finally {
      setInVolo(null);
    }
  };

  if (errore) return <p className="text-sm text-danger-foreground">{errore}</p>;
  if (!righe) return <Skeleton className="h-64 w-full rounded-xl" />;

  const scelti = righe.filter((o) => o.attivo && o.default_attivo);

  return (
    <Card className="p-card" data-testid="omaggi-tab">
      <h2 className="text-base font-semibold text-foreground">Omaggi di casa</h2>
      <p className="mt-micro max-w-2xl text-sm text-muted-foreground">
        Quelli che il preventivo propone. «Si offre» spento: non compare più. «Parte acceso»: è già
        selezionato, il venditore può toglierlo. Vale per i preventivi nuovi. L'omaggio fuori
        catalogo si scrive direttamente nel preventivo.
      </p>
      {righe.length === 0 ? (
        <p className="mt-group rounded-md bg-muted px-group py-group text-sm text-muted-foreground">
          Il catalogo degli omaggi è vuoto.
        </p>
      ) : (
        <ul className="-mx-card mt-group divide-y border-t">
          {righe.map((o) => (
            <li key={o.codice} className="px-card py-group" data-testid={`omaggio-${o.codice}`}>
              <p className="text-sm font-medium text-foreground">{o.etichetta}</p>
              {o.descrizione ? <p className="mt-micro text-sm text-muted-foreground">{o.descrizione}</p> : null}
              {o.nota ? <p className="mt-micro text-xs text-muted-foreground">{o.nota}</p> : null}
              <div className="mt-group flex flex-wrap gap-x-card gap-y-inside">
                <div className="flex min-h-11 items-center gap-inside">
                  <Switch
                    id={`omaggio-${o.codice}-attivo`}
                    checked={o.attivo}
                    disabled={inVolo !== null}
                    onCheckedChange={(v) => cambia(o.codice, "attivo", v)}
                    data-testid={`omaggio-${o.codice}-attivo`}
                    className="relative after:absolute after:inset-x-0 after:-inset-y-2.5 after:content-['']"
                  />
                  <Label htmlFor={`omaggio-${o.codice}-attivo`} className="cursor-pointer text-sm text-foreground">
                    Si offre
                  </Label>
                </div>
                <div className="flex min-h-11 items-center gap-inside">
                  <Switch
                    id={`omaggio-${o.codice}-default`}
                    checked={o.default_attivo}
                    disabled={inVolo !== null || !o.attivo}
                    onCheckedChange={(v) => cambia(o.codice, "default_attivo", v)}
                    data-testid={`omaggio-${o.codice}-default`}
                    className="relative after:absolute after:inset-x-0 after:-inset-y-2.5 after:content-['']"
                  />
                  <Label
                    htmlFor={`omaggio-${o.codice}-default`}
                    className={o.attivo ? "cursor-pointer text-sm text-foreground" : "text-sm text-muted-foreground"}
                  >
                    Parte acceso
                  </Label>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {righe.length ? (
        <p className="-mx-card -mb-card mt-0 border-t bg-surface-2 px-card py-group text-sm text-foreground" data-testid="omaggi-riepilogo">
          <span className="font-semibold">In un preventivo nuovo partono già scelti:</span>{" "}
          {scelti.length ? scelti.map((o) => o.etichetta).join(", ") : "nessuno"}.
        </p>
      ) : null}
    </Card>
  );
}
